import React, { useState } from 'react';
import { promotionGroups } from '../services/promotionGroups';

const PromotionGroupForm = ({ group, onSubmit, onClose }) => {
  const safeGroup = group ?? {};
  const safeDetails = safeGroup.groupDetails || safeGroup.group_details || [];
  const [name, setName] = useState(safeGroup.name || '');
  const [details, setDetails] = useState(
    safeDetails.length > 0
      ? safeDetails.map((detail) => ({
          bike_name: detail.bike_name || '',
          colors: Array.isArray(detail.colors) ? detail.colors : [],
          image: null,
          image_path: detail.image_path || null,
          image_url: detail.image_url || null,
        }))
      : [{ bike_name: '', colors: ['#ffffff'], image: null, image_path: null, image_url: null }]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateDetailField = (index, field, value) => {
    setDetails((prev) =>
      prev.map((detail, idx) => (idx === index ? { ...detail, [field]: value } : detail))
    );
  };

  const updateColor = (detailIndex, colorIndex, value) => {
    setDetails((prev) =>
      prev.map((detail, idx) => {
        if (idx !== detailIndex) return detail;
        const nextColors = [...detail.colors];
        nextColors[colorIndex] = value;
        return { ...detail, colors: nextColors };
      })
    );
  };

  const addColor = (detailIndex) => {
    setDetails((prev) =>
      prev.map((detail, idx) =>
        idx !== detailIndex ? detail : { ...detail, colors: [...detail.colors, '#ffffff'] }
      )
    );
  };

  const removeColor = (detailIndex, colorIndex) => {
    setDetails((prev) =>
      prev.map((detail, idx) => {
        if (idx !== detailIndex) return detail;
        const nextColors = detail.colors.filter((_, i) => i !== colorIndex);
        return { ...detail, colors: nextColors.length > 0 ? nextColors : ['#ffffff'] };
      })
    );
  };

  const addDetail = () => {
    setDetails((prev) => [...prev, { bike_name: '', colors: ['#ffffff'], image: null, image_path: null, image_url: null }]);
  };

  const removeDetail = (index) => {
    setDetails((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    data.append('name', name);
    details.forEach((detail, detailIndex) => {
      data.append(`details[${detailIndex}][bike_name]`, detail.bike_name);
      if (detail.image) {
        data.append(`details[${detailIndex}][image]`, detail.image);
      } else if (detail.image_path) {
        data.append(`details[${detailIndex}][image_path]`, detail.image_path);
      }
      detail.colors.forEach((color, colorIndex) => {
        data.append(`details[${detailIndex}][colors][${colorIndex}]`, color);
      });
    });
    try {
      if (onSubmit) {
        await onSubmit(data);
      } else {
        await promotionGroups.create(data);
      }
      onClose && onClose();
      setName('');
      setDetails([
        { bike_name: '', colors: ['#ffffff'], image: null, image_path: null, image_url: null },
      ]);
    } catch (err) {
      console.error('Save error:', err);
      const response = err.response?.data;
      let msg = response?.message || err.message || 'Terjadi kesalahan';
      alert('Gagal menyimpan grup promosi:\n' + msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 p-6">
          <h3 className="text-xl font-bold text-gray-900">
            {safeGroup.id ? 'Edit Grup Promosi' : 'Tambah Grup Promosi Baru'}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Kelola detail produk dan warna untuk grup promosi ini.
          </p>
        </div>

        <form onSubmit={handleCreate} className="p-6 space-y-6">
          {/* Nama Grup */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Nama Grup Promosi
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              placeholder="Contoh: Ramadhan 2024, Lebaran Special"
              required
            />
          </div>

          {/* Detail Produk */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              Detail Produk
            </label>
            <div className="space-y-4">
              {details.map((detail, detailIndex) => (
                <div
                  key={detailIndex}
                  className="border border-gray-200 rounded-xl p-5 bg-gray-50"
                >
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-semibold text-gray-700">
                      Produk #{detailIndex + 1}
                    </span>
                    {details.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDetail(detailIndex)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Hapus
                      </button>
                    )}
                  </div>

                  {/* Bike Name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nama Model/Tipe
                    </label>
                    <input
                      type="text"
                      value={detail.bike_name}
                      onChange={(e) =>
                        updateDetailField(detailIndex, 'bike_name', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                      placeholder="Contoh: SCOOPY, VARIO 125"
                      required
                    />
                  </div>

                  {/* Image */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Foto Produk
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        updateDetailField(
                          detailIndex,
                          'image',
                          file
                        );
                        if (file) {
                          updateDetailField(
                            detailIndex,
                            'image_url',
                            URL.createObjectURL(file)
                          );
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {(detail.image_url || detail.image_path) && (
                      <img
                        src={detail.image_url || detail.image_path}
                        alt={detail.bike_name || `Produk ${detailIndex + 1}`}
                        className="mt-3 w-24 h-24 object-cover rounded-lg border border-gray-300"
                      />
                    )}
                  </div>

                  {/* Colors */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pilihan Warna
                    </label>
                    <div className="space-y-2">
                      {detail.colors.map((color, colorIndex) => (
                        <div key={colorIndex} className="flex items-center gap-2">
                          <input
                            type="color"
                            value={color}
                            onChange={(e) =>
                              updateColor(detailIndex, colorIndex, e.target.value)
                            }
                            className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={color}
                            onChange={(e) =>
                              updateColor(detailIndex, colorIndex, e.target.value)
                            }
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => removeColor(detailIndex, colorIndex)}
                            className="text-red-600 hover:text-red-900 font-semibold"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => addColor(detailIndex)}
                      className="mt-3 text-sm text-blue-600 hover:text-blue-900 font-medium"
                    >
                      + Tambah Warna
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addDetail}
              className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-900 font-medium text-sm border border-blue-300 rounded-lg hover:bg-blue-50 transition"
            >
              + Tambah Produk
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 transition"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {isSubmitting ? 'Menyimpan...' : safeGroup.id ? 'Update' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromotionGroupForm;
